import {
	type MutableRefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	DisplayFieldFeedback,
	type DisplayFieldFeedback as DisplayFieldFeedbackType,
	FEEDBACK_SAVED_DISPLAY_MS,
	FEEDBACK_SAVED_FADE_MS,
} from "@/components/Atoms/DisplayField/display-field.shared";

export interface UseFieldFeedbackMapResult<TField extends string> {
	feedbackByField: Partial<Record<TField, DisplayFieldFeedbackType>>;
	setFieldFeedback: (field: TField, feedback: DisplayFieldFeedbackType) => void;
	/** Latest map without waiting for render (form autosave refs). */
	feedbackByFieldRef: MutableRefObject<
		Partial<Record<TField, DisplayFieldFeedbackType>>
	>;
}

/**
 * Owns per-field auto-save feedback and the Saved → End → Idle lifecycle.
 *
 * @returns Feedback map, setter, and a ref synced on every update.
 */
export function useFieldFeedbackMap<
	TField extends string = string,
>(): UseFieldFeedbackMapResult<TField> {
	const [feedbackByField, setFeedbackByField] = useState<
		Partial<Record<TField, DisplayFieldFeedbackType>>
	>({});
	const feedbackByFieldRef = useRef(feedbackByField);
	const savedClearTimers = useRef<
		Partial<Record<TField, ReturnType<typeof setTimeout>>>
	>({});

	useEffect(() => {
		return () => {
			const timers: Array<ReturnType<typeof setTimeout> | undefined> =
				Object.values(savedClearTimers.current);
			for (const timer of timers) {
				if (timer) clearTimeout(timer);
			}
		};
	}, []);

	const setFieldFeedback = useCallback(
		(field: TField, feedback: DisplayFieldFeedbackType) => {
			const existing = savedClearTimers.current[field];
			if (existing) clearTimeout(existing);
			savedClearTimers.current[field] = undefined;

			feedbackByFieldRef.current = {
				...feedbackByFieldRef.current,
				[field]: feedback,
			};
			setFeedbackByField((prev) => ({ ...prev, [field]: feedback }));

			if (feedback !== DisplayFieldFeedback.Saved) return;

			savedClearTimers.current[field] = setTimeout(() => {
				feedbackByFieldRef.current = {
					...feedbackByFieldRef.current,
					[field]: DisplayFieldFeedback.End,
				};
				setFeedbackByField((prev) => ({
					...prev,
					[field]: DisplayFieldFeedback.End,
				}));
				savedClearTimers.current[field] = setTimeout(() => {
					feedbackByFieldRef.current = {
						...feedbackByFieldRef.current,
						[field]: DisplayFieldFeedback.Idle,
					};
					setFeedbackByField((prev) => ({
						...prev,
						[field]: DisplayFieldFeedback.Idle,
					}));
					savedClearTimers.current[field] = undefined;
				}, FEEDBACK_SAVED_FADE_MS);
			}, FEEDBACK_SAVED_DISPLAY_MS);
		},
		[],
	);

	return {
		feedbackByField,
		setFieldFeedback,
		feedbackByFieldRef,
	};
}
